import { UseFormReturnType } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import * as Sentry from "@sentry/react"
import { useMutation, useQuery } from "@tanstack/react-query"

import { DatadogInstallation } from "@costory/types/endpoints/integration"
import {
  AwsDatasourceFormData,
  BQValidationType,
  DatadogCIVisibilityValidationFormData,
  GCSDatasouceCreationFormData,
  NewDatadogValidationFormData,
  MetricsDatasourceRequests,
  MetricsDatasourceResponses,
  DatadogValidationFormData,
  GSheetDatasourceFormData,
} from "@costory/types/endpoints/metricsDatasources"

import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"

export const useMetricsDatasourcesListQuery = () => {
  return useQuery({
    queryKey: ["metricsDatasources"],
    queryFn: async () => {
      const response =
        await apiClient.get<MetricsDatasourceResponses.GetAllMetrics>(
          "/metricsDatasources",
        )
      return response.data
    },
  })
}
export const useMetricsDatasourcesListWithMetricsDefinitionQuery = () => {
  return useQuery({
    queryKey: ["metricsDatasources", "WithMetricsDefinition"],
    queryFn: async () => {
      const response =
        await apiClient.get<MetricsDatasourceResponses.GetAllMetrics>(
          "/metricsDatasources/with-metrics-definition",
        )
      return response.data
    },
  })
}

export const useCreateMetricsDatasourceMutation = () => {
  return useMutation({
    mutationKey: ["createMetricsDatasource"],
    mutationFn: async (
      data: MetricsDatasourceRequests.CreateMetricsDatasource,
    ) => {
      await apiClient.post<
        null,
        null,
        MetricsDatasourceRequests.CreateMetricsDatasource
      >("/metricsDatasources", data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["metricsDatasources"] })
      await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
      showNotification({
        title: "Success",
        message:
          "Datasource created: you can now create metrics from the datasource using the button +",
        color: "green",
      })
    },
  })
}

export const useDeleteDatasourceMutation = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/metricsDatasources/${id}`)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["metricsDatasources"] })
      showNotification({
        title: "Success",
        message: "Datasource deleted successfully",
        color: "green",
      })
    },
    onError: (error) => {
      showNotification({
        title: "Error",
        message: error.message,
        color: "red",
      })
    },
  })
}

export const useValidateGoogleSheetQuery = (id: string) => {
  return useQuery({
    queryKey: ["validateGoogleSheet", id],
    queryFn: async () => {
      const response =
        await apiClient.get<MetricsDatasourceResponses.ValidateGoogleSheet>(
          `/metricsDatasources/gsheet/validate/${id}`,
        )
      return response.data
    },
  })
}

export const useCreateGoogleSheetMutation = ({
  setGsheetIdCreated,
  form,
}: {
  setGsheetIdCreated: (gsheetId: string) => void
  form: UseFormReturnType<GSheetDatasourceFormData>
}) => {
  return useMutation({
    mutationFn: async () => {
      const response =
        await apiClient.get<MetricsDatasourceResponses.CreateGoogleSheet>(
          "/metricsDatasources/gsheet/create/",
        )
      return response.data
    },
    onSuccess: (gsheetId: MetricsDatasourceResponses.CreateGoogleSheet) => {
      setGsheetIdCreated(gsheetId.gsheetId)
      form.setFieldValue("gSheetId", gsheetId.gsheetId)
    },
    onError: () => {
      showNotification({
        title: "Error",
        message: "Failed to create Google Sheet, please contact support",
        color: "red",
      })
      Sentry.captureException(new Error("Failed to create Google Sheet"))
    },
  })
}

export const useValidateDatadog = (data: DatadogValidationFormData) => {
  return useQuery({
    queryKey: ["validateDatadog", data.query, data.datadogConfig, data.name],
    queryFn: async () => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ValidateDatadog>(
          "/metricsDatasources/datadog/validate/",
          data,
        )
      return response.data
    },
  })
}
export const useValidateDatadogMutation = () => {
  return useMutation({
    mutationKey: ["validateDatadogMutation"],
    mutationFn: async (data: NewDatadogValidationFormData) => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ValidateDatadog>(
          "/metricsDatasources/datadog/validate/",
          data,
        )
      return response.data
    },
  })
}

export const useValidateBigQuery = (data: BQValidationType) => {
  return useQuery({
    queryKey: ["validateBigQuery", data],
    queryFn: async () => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ValidateBigQuery>(
          "/metricsDatasources/bigquery/validate",
          data,
        )
      return response.data
    },
  })
}

export const useValidateGCS = (
  data: Pick<
    GCSDatasouceCreationFormData,
    "inventoryFilePath" | "bucketName" | "tagSearchPattern"
  >,
) => {
  return useQuery({
    queryKey: ["validateGCS", data],
    queryFn: async () => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ValidateGCS>(
          "/metricsDatasources/gcs/validate",
          data,
        )
      return response.data
    },
  })
}

export const useValidateDatadogCIVisibility = (
  data: DatadogCIVisibilityValidationFormData,
) => {
  return useQuery({
    queryKey: ["validateDatadogCIVisibility", data],
    queryFn: async () => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ValidateDatadog>(
          "/metricsDatasources/datadog-ci-visibility/validate/",
          data,
        )
      return response.data
    },
  })
}

export const useValidateAws = (data: AwsDatasourceFormData) => {
  return useQuery({
    queryKey: ["importFileFromContainer", data],
    queryFn: async () => {
      const response =
        await apiClient.post<MetricsDatasourceResponses.ParquetRecord>(
          "/metricsDatasources/aws/import-file-from-container",
          data,
        )
      return response.data
    },
  })
}

export const usePreviewMetricsDatasourceQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: ["metricsDatasources", "preview", id],
    enabled: !!id,
    queryFn: async () => {
      const response =
        await apiClient.get<MetricsDatasourceResponses.PreviewMetricsDatasource>(
          `/metricsDatasources/${id}/preview`,
        )
      return response.data
    },
  })
}

export const useDatadogInstallationsQuery = () => {
  return useQuery({
    queryKey: ["datadogInstallations"],
    queryFn: async () => {
      const response = await apiClient.get<DatadogInstallation>(
        "/metricsDatasources/datadog-installations",
      )
      return response.data
    },
  })
}
